import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import InformationList from '../InformationList/InformationList';
import InformationListItemColor from '../InformationList/InformationListItemWithColor';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import Paragraph from '../Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';
import creditCardTypes from '../../util/enum/api/creditCardTypes';

const PricesWrapper = styled.div``;

const OrderTotal = ({
  finalOrder,
  orderType,
  orderValues,
  buyCashBuffer,
  paymentMethod,
  isPostPaidVisible,
}) => {
  const { t } = useTranslation();
  const settings = useSelector(selectSettings);
  const [
    cashOrderInformationListData,
    setCashOrderInformationListData,
  ] = useState([]);
  const [
    quantityOrderInformationListData,
    setQuantityOrderInformationListData,
  ] = useState([]);
  const [priceLocked, setPriceLocked] = useState(false);

  const paymentMethodValue = () => {
    switch (paymentMethod?.PaymentMethodType) {
      case 1:
        return t('buyWizard.postPaid.paymentMethods.AccountBalance');
      case 2:
        return `${paymentMethod?.BankName}, ${paymentMethod?.BankAccountMask}`;
      case 3:
        return t('creditCards.cardTypeAndNumberLabel', {
          cardType: t(
            `creditCards.creditCardTypes.${
              creditCardTypes[paymentMethod?.CreditCardType]
            }`,
          ),
          last4Digits: paymentMethod?.Last4Digits,
        });

      default:
        return t('buyWizard.postPaid.paymentMethods.AccountBalance');
    }
  };

  const showPaymentMethodValue = paymentMethodValue();

  useEffect(() => {
    if (!settings || isEmpty(settings) || !settings?.BrokerCode) {
      return;
    }
    setPriceLocked(!!settings?.IsLockedPricingActive);
  }, [settings]);

  // const discountPrice =
  //   finalOrder.paymentMethodData?.DataWithoutCCFee?.TotalClientExecutionPrice;
  const crossedOutPrice =
    finalOrder.paymentMethodData?.DataWithoutCCFee
      ?.TotalClientExecutionPriceWithFee;

  const discountTotal =
    Math.abs(finalOrder.volumeDiscount) +
    (isPostPaidVisible ? finalOrder.paymentTypeDiscount : 0);

  // const discountTotalPrice =
  //   (isPostPaidVisible ? crossedOutPrice : finalOrder.estimatedTotal) -
  //   discountTotal;

  const clientExecutionPrice =
    paymentMethod?.PaymentMethodType === 3
      ? finalOrder.paymentMethodData?.DataWithCCFee?.TotalClientExecutionPrice
      : finalOrder.paymentMethodData?.DataWithoutCCFee
          ?.TotalClientExecutionPrice;
  const priceWithDiscount = () => {
    if (paymentMethod?.PaymentMethodType !== 3) {
      return (
        <PricesWrapper>
          {/* <Paragraph color={themeColors.colorSuccess} bold>
            {formatMoneyNumeral(discountPrice)}
          </Paragraph> */}
          <Paragraph textDecoration="line-through" marginLeft={8} bold>
            {formatMoneyNumeral(crossedOutPrice)}
          </Paragraph>
        </PricesWrapper>
      );
    }
    return (
      <Paragraph bold>
        {formatMoneyNumeral(finalOrder.estimatedTotal)}
      </Paragraph>
    );
  };

  // const formatDiscountTotal = () => (
  //   <Paragraph bold>{formatMoneyNumeral(discountTotalPrice)}</Paragraph>
  // );

  const formatedEstimatedPrice = () => (
    <Paragraph
      textDecoration={isPostPaidVisible ? 'line-through' : ''}
      marginLeft={8}
      bold
    >
      {formatMoneyNumeral(finalOrder.estimatedTotal)}
    </Paragraph>
  );

  const discountedItem = (discountAmount) => (
    <PricesWrapper>
      <Paragraph color={themeColors.colorSuccess} bold>
        -{formatMoneyNumeral(discountAmount)}
      </Paragraph>
    </PricesWrapper>
  );

  useEffect(() => {
    const dataForCashOrder = [
      {
        value: formatMoneyNumeral(finalOrder.pricePerUnit),
        label: settings.IsLockedPricingActive
          ? t('product.detailsTable.pricePerUnit')
          : t('buyWizard.buyForDelivery.reviewOrder.orderTotal.pricePerUnit'),
      },
      {
        value: finalOrder.quantity,
        label: settings.IsLockedPricingActive
          ? t('buyWizard.buyForDelivery.reviewOrder.orderTotal.quantity')
          : t(
              'buyWizard.buyForDelivery.reviewOrder.orderTotal.estimatedQuantity',
            ),
      },
      // discountTotal > 0
      //   ? {
      //       value: formatMoneyNumeral(discountTotalPrice),
      //       label: settings.IsLockedPricingActive
      //         ? t('buyWizard.buyForDelivery.reviewOrder.orderTotal.subtotal')
      //         : t('buyWizard.buyForDelivery.reviewOrder.orderTotal.subtotal'),
      //       isBold: true,
      //     }
      //   : { value: '', label: '' },
      settings?.IsLockedPricingActive &&
      Math.abs(finalOrder?.volumeDiscount) > 0
        ? {
            value: discountedItem(Math.abs(finalOrder?.volumeDiscount)),
            label: t(
              'buyWizard.buyForDelivery.reviewOrder.orderTotal.volumeDiscount',
            ),
            isBold: true,
            isColored: true,
          }
        : { value: '', label: '' },
      settings?.IsLockedPricingActive &&
      isPostPaidVisible &&
      finalOrder?.paymentTypeDiscount > 0
        ? {
            value: discountedItem(finalOrder?.paymentTypeDiscount),
            label: t(
              'buyWizard.buyForDelivery.reviewOrder.orderTotal.cashDiscount',
            ),
            isBold: true,
            isColored: true,
          }
        : { value: '', label: '' },
      {
        value: isPostPaidVisible
          ? priceWithDiscount()
          : formatMoneyNumeral(finalOrder.estimatedTotal),
        label: settings.IsLockedPricingActive
          ? t('buyWizard.buyForDelivery.reviewOrder.orderTotal.total')
          : t('buyWizard.buyForDelivery.reviewOrder.orderTotal.estimatedTotal'),
        isBold: true,
      },
    ];
    setCashOrderInformationListData(dataForCashOrder);

    const dataForQuantityOrder = [
      {
        value: formatMoneyNumeral(finalOrder.pricePerUnit),
        label: settings.IsLockedPricingActive
          ? t('product.detailsTable.pricePerUnit')
          : t('buyWizard.buyForDelivery.reviewOrder.orderTotal.pricePerUnit'),
      },
      {
        value: finalOrder.quantity,
        label: t('buyWizard.buyForDelivery.reviewOrder.orderTotal.quantity'),
      },
      // discountTotal > 0
      //   ? {
      //       value: formatMoneyNumeral(discountTotalPrice),
      //       label: t(
      //         'buyWizard.buyForDelivery.reviewOrder.orderTotal.subtotal',
      //       ),
      //       isBold: true,
      //     }
      //   : { label: '', value: '' },

      settings?.IsLockedPricingActive &&
      Math.abs(finalOrder?.volumeDiscount) > 0
        ? {
            value: discountedItem(Math.abs(finalOrder?.volumeDiscount)),
            label: t(
              'buyWizard.buyForDelivery.reviewOrder.orderTotal.volumeDiscount',
            ),
            isBold: false,
            isColored: true,
          }
        : { label: '', value: '' },
      settings?.IsLockedPricingActive &&
      isPostPaidVisible &&
      finalOrder?.paymentTypeDiscount > 0
        ? {
            value: discountedItem(finalOrder?.paymentTypeDiscount),
            label: t(
              'buyWizard.buyForDelivery.reviewOrder.orderTotal.cashDiscount',
            ),
            isBold: false,
            isColored: true,
          }
        : { label: '', value: '' },

      ...(discountTotal
        ? [
            {
              label: t('buyWizard.buyForDelivery.reviewOrder.orderTotal.total'),
              value: isPostPaidVisible
                ? priceWithDiscount()
                : formatedEstimatedPrice(),
              isBold: false,
            },
            isPostPaidVisible
              ? {
                  label: t(
                    'buyWizard.buyForDelivery.reviewOrder.orderTotal.discountedTotal',
                  ),
                  value: formatMoneyNumeral(clientExecutionPrice),

                  isBold: true,
                }
              : {},
          ]
        : [
            {
              // Only show the total when there is no discount
              label: t(
                'buyWizard.buyForDelivery.reviewOrder.orderTotal.actualTotal',
              ),
              value: formatMoneyNumeral(finalOrder.estimatedTotal),
              isBold: true,
            },
          ]),
      isPostPaidVisible
        ? {
            value: showPaymentMethodValue,
            label: t('buyWizard.verifyOrder.paymentMethod'),
          }
        : { label: '', value: '' },
    ];
    setQuantityOrderInformationListData(dataForQuantityOrder);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyCashBuffer, orderValues, orderType, finalOrder, settings]);

  if (orderType === 'Cash' && !priceLocked) {
    return (
      <>
        <InformationList marginTop={8} withBackground>
          {cashOrderInformationListData.map(
            ({ value, label, isBold, isColored }) => (
              <InformationListItemColor
                key={uuidv4()}
                fullWidth
                isBold={isBold}
                value={value}
                isColored={isColored}
                label={label}
              />
            ),
          )}
        </InformationList>
      </>
    );
  }

  if (orderType === 'Quantity' || priceLocked) {
    return (
      <>
        <InformationList marginBottom={8} marginTop={8} withBackground>
          {quantityOrderInformationListData.map(
            ({ label, value, isBold, isColored }) => (
              <InformationListItemColor
                key={uuidv4()}
                label={label}
                value={value}
                isColored={isColored}
                isBold={isBold}
                fullWidth
              />
            ),
          )}
        </InformationList>
      </>
    );
  }

  return null;
};

OrderTotal.propTypes = {
  finalOrder: PropTypes.shape({
    pricePerUnit: PropTypes.number,
    quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    estimatedTotal: PropTypes.number,
    cashBuffer: PropTypes.number,
    requiredBalanceWithBuffer: PropTypes.number,
    estimatedPriceForUnit: PropTypes.number,
    estimatedQuantity: PropTypes.number,
    volumeDiscount: PropTypes.number,
    newPricePerUnit: PropTypes.number,
    paymentTypeDiscount: PropTypes.number,
    paymentMethodData: PropTypes.shape({
      DataWithoutCCFee: PropTypes.shape({
        TotalClientExecutionPrice: PropTypes.number,
        TotalClientExecutionPriceWithFee: PropTypes.number,
      }),
      DataWithCCFee: PropTypes.shape({
        TotalClientExecutionPrice: PropTypes.number,
        TotalClientExecutionPriceWithFee: PropTypes.number,
      }),
      TotalClientExecutionPrice: PropTypes.number,
    }),
  }),
  paymentMethod: PropTypes.shape({
    PaymentMethodType: PropTypes.number,
    BankName: PropTypes.string,
    BankAccountMask: PropTypes.number,
    CreditCardType: PropTypes.number,
    Last4Digits: PropTypes.number,
  }),
  isPostPaidVisible: PropTypes.bool,
  orderType: PropTypes.string,
  orderValues: PropTypes.shape({
    CashAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    QuantityAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  buyCashBuffer: PropTypes.number,
  TotalClientExecutionPrice: PropTypes.number,
};

export default React.memo(OrderTotal);
